
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { ChevronDown } from "lucide-react";
import { useSubscriptionCategories } from "@/hooks/useSubscriptionCategories";
import CategoryGroup from "./CategoryGroup";
import SelectedCategories from "./SelectedCategories";
import { useCategoryGroups } from "@/hooks/useCategoryGroups";
import { useCategories } from "@/hooks/useCategories";

interface CategorySelectionProps {
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
}

export default function CategorySelection({ 
  selectedCategories, 
  setSelectedCategories 
}: CategorySelectionProps) {
  const { categoryGroups } = useCategoryGroups();
  const { categories, isLoading: categoriesLoading } = useCategories();
  const { categoryMap } = useSubscriptionCategories();
  const [categoriesByGroup, setCategoriesByGroup] = useState<Record<string, any[]>>({});
  const [ungroupedCategories, setUngroupedCategories] = useState<any[]>([]);
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  // Organize categories by group
  useEffect(() => {
    if (categories && categoryGroups) {
      const grouped: Record<string, any[]> = {};
      const ungrouped: any[] = [];
      
      // Initialize groups
      categoryGroups.forEach(group => {
        grouped[group.id] = [];
      });
      
      // Populate groups with categories
      categories.forEach(category => {
        if (category.categoryGroupId && grouped[category.categoryGroupId]) {
          grouped[category.categoryGroupId].push(category);
        } else {
          ungrouped.push(category);
        }
      });
      
      setCategoriesByGroup(grouped);
      setUngroupedCategories(ungrouped);

      // Determine which groups should be opened by default
      const groupsWithActiveCategories = new Set<string>();
      
      // Find groups with selected categories
      selectedCategories.forEach(catId => {
        const category = categories.find(c => c.id === catId);
        if (category?.categoryGroupId) {
          groupsWithActiveCategories.add(category.categoryGroupId);
        } else if (category) {
          groupsWithActiveCategories.add('ungrouped');
        }
      });
      
      if (groupsWithActiveCategories.size > 0) {
        // Open all groups with active categories
        setOpenGroups(Array.from(groupsWithActiveCategories));
      } else if (categoryGroups.length > 0) {
        // If no active categories, open the first group
        setOpenGroups([categoryGroups[0].id]);
      } else if (ungrouped.length > 0) {
        // If no groups but there are ungrouped categories, open that
        setOpenGroups(['ungrouped']);
      }
    }
  }, [categories, categoryGroups, selectedCategories]);

  const toggleCategory = (id: string) => {
    setSelectedCategories(
      selectedCategories.includes(id)
        ? selectedCategories.filter(c => c !== id)
        : [...selectedCategories, id]
    );
  };
  
  const removeSelectedCategory = (categoryId: string) => {
    setSelectedCategories(selectedCategories.filter(id => id !== categoryId));
  };
  
  const handleClearAll = () => {
    setSelectedCategories([]);
  };
  
  const handleSelectAll = () => {
    const allCategoryIds = categories ? categories.map(cat => cat.id) : [];
    setSelectedCategories(allCategoryIds);
  };

  if (categoriesLoading) {
    return <div>Loading categories...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Categories</h3>
        <div className="space-x-2 text-sm">
          <button 
            type="button"
            onClick={handleClearAll}
            className="text-blue-600 hover:underline"
          >
            Clear All
          </button>
          {" | "}
          <button 
            type="button"
            onClick={handleSelectAll}
            className="text-blue-600 hover:underline"
          >
            Select All
          </button>
        </div>
      </div>
      
      <SelectedCategories
        selectedCategories={selectedCategories}
        categoryMap={categoryMap}
        onRemoveCategory={removeSelectedCategory}
      />
      
      <div className="border rounded-md">
        <Accordion type="multiple" value={openGroups} onValueChange={setOpenGroups} className="w-full">
          {categoryGroups?.map(group => (
            <AccordionItem key={group.id} value={group.id}>
              <AccordionTrigger className="px-4 hover:no-underline">
                {group.name}
              </AccordionTrigger>
              <AccordionContent>
                <CategoryGroup
                  categories={categoriesByGroup[group.id] || []}
                  selectedCategories={selectedCategories}
                  onToggleCategory={toggleCategory}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
          
          {ungroupedCategories.length > 0 && (
            <AccordionItem value="ungrouped">
              <AccordionTrigger className="px-4 hover:no-underline">
                Other Categories
              </AccordionTrigger>
              <AccordionContent>
                <CategoryGroup
                  categories={ungroupedCategories}
                  selectedCategories={selectedCategories}
                  onToggleCategory={toggleCategory}
                />
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </div>
    </div>
  );
}
